import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Accordion, AccordionSummary, AccordionDetails, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

/**
 * InfoCardStack
 * Reusable vertical stack of informational cards (accordion-style).
 * Props:
 * - cards: [{ id, title, subtitle?, content }] where content may be string or node
 * - allowMultipleOpen: boolean (default: false) - if true permite abrir varios a la vez
 */
export default function InfoCardStack({ cards, allowMultipleOpen = false }) {
  const [expanded, setExpanded] = useState(allowMultipleOpen ? {} : null);

  const handleChange = (id) => (event, isOpen) => {
    if (allowMultipleOpen) {
      setExpanded((prev) => ({ ...prev, [id]: !!isOpen }));
    } else {
      setExpanded((prev) => (prev === id ? null : id));
    }
  };

  return (
    <Box>
      {cards.map((c) => (
        <Accordion
          key={c.id}
          expanded={allowMultipleOpen ? !!expanded[c.id] : expanded === c.id}
          onChange={handleChange(c.id)}
          disableGutters
          elevation={0}
          sx={{
            mb: { xs: 1, sm: 1.5 },
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            overflow: 'hidden',
            background: (theme) => theme.palette.background.paper,
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls={`${c.id}-content`}
            id={`${c.id}-header`}
            sx={{ background: (theme) => theme.palette.background.paper, px: { xs: 2, sm: 3 } }}
          >
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: { xs: 16, sm: 18 } }}>{c.title}</Typography>
              {c.subtitle && (
                <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>{c.subtitle}</Typography>
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ px: { xs: 2, sm: 3 }, pb: 2 }}>
            {typeof c.content === 'string' ? (
              <Typography sx={{ whiteSpace: 'pre-line', color: 'text.primary', lineHeight: 1.6 }}>
                {c.content}
              </Typography>
            ) : (
              <Box sx={{ '& > *': { lineHeight: 1.6 } }}>{c.content}</Box>
            )}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}

InfoCardStack.propTypes = {
  cards: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      title: PropTypes.string.isRequired,
      subtitle: PropTypes.string,
      content: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
    })
  ).isRequired,
  allowMultipleOpen: PropTypes.bool,
};
